import React, { useEffect, useState } from "react";
import { AiFillPlusCircle } from "react-icons/ai";
import Header from "../header/Header";
import Sidebar from "../Sidebar/Sidebar";
import {
  getAllUsersAdmin,
  getAllRoles,
  updateUserRole,
  deleteUser,
} from "../../services/ApiService";
import { Button, Input, Select, Table, message, Popconfirm } from "antd";
import { SearchOutlined, ClearOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { hasPermission } from "../GlobalFunction";
import "../Fields/FieldList.css";

const UserList = () => {
  const navigate = useNavigate();
  const canCreate = hasPermission("users", "create");
  const canUpdate = hasPermission("users", "update");
  const canDelete = hasPermission("users", "delete");
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchName, setSearchName] = useState("");
  const [searchEmpId, setSearchEmpId] = useState("");
  const [searchEmail, setSearchEmail] = useState("");
  const [selectedRole, setSelectedRole] = useState(undefined);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await getAllUsersAdmin();
      if (res.responseCode === 200) {
        setUsers(res.user_list || []);
      } else {
        message.error(res.responseMessage || "Failed to fetch users");
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      message.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const res = await getAllRoles();
      if (res.responseCode === 200) {
        setRoles(res.data || []);
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  const handleRoleChange = async (userId, roleId) => {
    try {
      const res = await updateUserRole({ user_id: userId, role_id: roleId });
      if (res.responseCode === 200) {
        message.success("Role updated successfully");
        setUsers((prev) =>
          prev.map((u) =>
            u.id === userId ? { ...u, role_id: roleId, role_name: res.data.role_name } : u
          )
        );
      } else {
        message.error(res.responseMessage || "Failed to update role");
      }
    } catch (error) {
      console.error("Error updating role:", error);
      message.error("Failed to update role");
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await deleteUser(id);
      if (res.responseCode === 200) {
        message.success("User deactivated successfully");
        fetchUsers();
      } else {
        message.error(res.message || "Failed to deactivate user");
      }
    } catch (error) {
      console.error("Error deactivating user:", error);
      message.error("Failed to deactivate user");
    }
  };

  const filteredUsers = users.filter((u) => {
    const fullName = [u.employee_first_name, u.employee_middle_name, u.employee_last_name]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    const matchesName = fullName.includes(searchName.trim().toLowerCase());
    const matchesEmpId = (u.employee_id || "")
      .toString()
      .toLowerCase()
      .includes(searchEmpId.trim().toLowerCase());
    const matchesEmail = (u.email_id || "")
      .toLowerCase()
      .includes(searchEmail.trim().toLowerCase());
    const matchesRole = !selectedRole || u.role_id === selectedRole;
    return matchesName && matchesEmpId && matchesEmail && matchesRole;
  });

  const hasActiveFilters =
    searchName || searchEmpId || searchEmail || selectedRole !== undefined;

  const clearFilters = () => {
    setSearchName("");
    setSearchEmpId("");
    setSearchEmail("");
    setSelectedRole(undefined);
  };

  const columns = [
    {
      title: "Sr. No",
      key: "sr",
      render: (_, __, index) => index + 1,
    },
    {
      title: "Name",
      key: "name",
      render: (_, record) =>
        [record.employee_first_name, record.employee_middle_name, record.employee_last_name]
          .filter(Boolean)
          .join(" "),
    },
    {
      title: "Employee ID",
      dataIndex: "employee_id",
      key: "employee_id",
    },
    {
      title: "Email",
      dataIndex: "email_id",
      key: "email_id",
    },
    {
      title: "Role",
      key: "role",
      render: (_, record) =>
        canUpdate ? (
          <Select
            value={record.role_id}
            style={{ width: 220 }}
            options={roles.map((r) => ({ label: r.role_name, value: r.id }))}
            onChange={(value) => handleRoleChange(record.id, value)}
            placeholder="No role assigned"
          />
        ) : (
          record.role_name || "No role assigned"
        ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) =>
        canDelete && (
          <Popconfirm
            title="Deactivate this user?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="Cancel"
          >
            <Button color="danger">Deactivate</Button>
          </Popconfirm>
        ),
    },
  ];

  return (
    <div className="main-box">
      <Sidebar activeId={12} />
      <main className="main-content">
        <div style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
          <Header title="User Management" />
          <div style={{ flex: 1, padding: "1.5rem", boxSizing: "border-box", overflowY: "auto" }}>
            {canCreate && (
              <div style={{ display: "flex", justifyContent: "center" }}>
                <div className="field-box" onClick={() => navigate("/add-user")}>
                  <AiFillPlusCircle color="#534ECC" />
                  <span style={{ color: "#344767", fontSize: "1rem", fontWeight: 700 }}>
                    {" "}Add User{" "}
                  </span>
                </div>
              </div>
            )}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "0.75rem",
                marginTop: "1rem",
                alignItems: "center",
              }}
            >
              <Input
                placeholder="Name"
                prefix={<SearchOutlined />}
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                style={{ width: 200 }}
                allowClear
              />
              <Input
                placeholder="Employee ID"
                prefix={<SearchOutlined />}
                value={searchEmpId}
                onChange={(e) => setSearchEmpId(e.target.value)}
                style={{ width: 180 }}
                allowClear
              />
              <Input
                placeholder="Email"
                prefix={<SearchOutlined />}
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
                style={{ width: 220 }}
                allowClear
              />
              <Select
                placeholder="Role"
                value={selectedRole}
                onChange={(value) => setSelectedRole(value)}
                options={roles.map((r) => ({ label: r.role_name, value: r.id }))}
                style={{ width: 200 }}
                allowClear
              />
              {hasActiveFilters && (
                <Button icon={<ClearOutlined />} onClick={clearFilters}>
                  Clear Filters
                </Button>
              )}
            </div>
            <Table
              columns={columns}
              dataSource={filteredUsers}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 10 }}
              scroll={{ x: "max-content" }}
              style={{
                marginTop: "1rem",
                fontSize: "0.8rem",
                borderRadius: "1.5rem",
                background: "#FFF",
                boxShadow: "0px 1.72px 5.161px 0px rgba(0, 0, 0, 0.25)",
              }}
              className="custom-scrollbar"
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default UserList;
